export type ReviewSelectionState = "checked" | "mixed" | "unchecked";

export const getReviewSelectionState = (
  refs: readonly string[],
  excludedRefs: ReadonlySet<string>,
): ReviewSelectionState => {
  if (!refs.length) return "unchecked";
  const included = refs.filter((ref) => !excludedRefs.has(ref)).length;
  if (included === refs.length) return "checked";
  if (included === 0) return "unchecked";
  return "mixed";
};

export const setReviewRefsIncluded = ({
  excludedRefs,
  refs,
  included,
  wallOpeningRefs = new Map<string, readonly string[]>(),
}: {
  excludedRefs: ReadonlySet<string>;
  refs: readonly string[];
  included: boolean;
  wallOpeningRefs?: ReadonlyMap<string, readonly string[]>;
}) => {
  const next = new Set(excludedRefs);
  refs.forEach((ref) => {
    if (included) next.delete(ref);
    else next.add(ref);
    if (!included) {
      wallOpeningRefs.get(ref)?.forEach((openingRef) => next.add(openingRef));
    }
  });
  return next;
};

export const toggleReviewRef = ({
  excludedRefs,
  ref,
  wallOpeningRefs,
}: {
  excludedRefs: ReadonlySet<string>;
  ref: string;
  wallOpeningRefs?: ReadonlyMap<string, readonly string[]>;
}) =>
  setReviewRefsIncluded({
    excludedRefs,
    refs: [ref],
    included: excludedRefs.has(ref),
    wallOpeningRefs,
  });

export const normalizeDegrees = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  return ((((value + 180) % 360) + 360) % 360) - 180;
};

export const radiansToDegrees = (value: number) =>
  normalizeDegrees((value * 180) / Math.PI);

export const degreesToRadians = (value: number) =>
  (normalizeDegrees(value) * Math.PI) / 180;

export const insertionSummary = ({
  structureRefs,
  symbolRefs,
  annotationRefs,
  excludedRefs,
}: {
  structureRefs: readonly string[];
  symbolRefs: readonly string[];
  annotationRefs: readonly string[];
  excludedRefs: ReadonlySet<string>;
}) => ({
  structure: structureRefs.filter((ref) => !excludedRefs.has(ref)).length,
  symbols: symbolRefs.filter((ref) => !excludedRefs.has(ref)).length,
  annotations: annotationRefs.filter((ref) => !excludedRefs.has(ref)).length,
});
